import { computed, inject, Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { AuthenticationService } from '../services/authentication.service';
import { DotNetRoleClaim, Permission } from './permissions';

type ClaimValue = string | string[] | undefined;
type AccessTokenClaims = Record<string, ClaimValue>;

@Injectable({ providedIn: 'root' })
export class AuthorizationService {
  private readonly authentication = inject(AuthenticationService);

  readonly roles = computed(() => this.readClaim(this.decode(), DotNetRoleClaim));
  readonly permissions = computed(() => this.readClaim(this.decode(), 'permission'));

  hasPermission(permission: Permission | string): boolean {
    return this.permissions().includes(permission);
  }

  hasAnyPermission(...permissions: Array<Permission | string>): boolean {
    return permissions.some((permission) => this.hasPermission(permission));
  }

  hasAllPermissions(...permissions: Array<Permission | string>): boolean {
    return permissions.every((permission) => this.hasPermission(permission));
  }

  hasRole(role: string): boolean {
    return this.roles().some((assignedRole) => assignedRole.toLowerCase() === role.toLowerCase());
  }

  hasAnyRole(...roles: string[]): boolean {
    return roles.some((role) => this.hasRole(role));
  }

  private decode(): AccessTokenClaims {
    const token = this.authentication.accessTokenSignal();
    if (!token) return {};

    try {
      return jwtDecode<AccessTokenClaims>(token);
    } catch {
      return {};
    }
  }

  /** The JWT decoder can emit either a scalar or an array for repeated claims. */
  private readClaim(claims: AccessTokenClaims, claimName: string): string[] {
    const value = claims[claimName];
    const values = Array.isArray(value) ? value : value ? [value] : [];
    return [...new Set(values.filter((item): item is string => typeof item === 'string' && !!item.trim()))];
  }
}
